import React, { useState, useEffect } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LogOut, Menu, Truck, X, LayoutDashboard } from "lucide-react";
import Logo from "@/components/layout/Logo";

export default function TopBar({ onMenuClick }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // Portal chrome (solid header, sidebar hamburger, thinner bar) applies only on
  // the dashboard itself — public pages stay transparent even when logged in.
  const isPortal = location.pathname.startsWith("/dashboard");
  const [publicMenuOpen, setPublicMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    onScroll(); // initialise on mount
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const onLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <>
      <header className={`df-header w-full sticky top-0 z-40${isPortal ? " portal" : scrolled ? " scrolled" : ""}`}>
        <div className={`w-full max-w-[1500px] mx-auto px-6 sm:px-10 lg:px-16 ${isPortal ? "py-3" : "py-4"} relative flex items-center justify-between`}>

          {/* ── Mobile hamburger — pinned left ────────────── */}
          <div className="absolute left-6 sm:left-10 lg:left-16 flex items-center md:hidden">
            {isPortal && user && user.role !== "driver" && (
              <button
                className="min-w-[44px] min-h-[44px] flex items-center justify-center rounded-lg text-white/70 hover:text-white hover:bg-white/10 transition-colors"
                onClick={onMenuClick}
                aria-label="Open navigation menu"
                data-testid="mobile-menu-btn"
              >
                <Menu size={22} />
              </button>
            )}
            {!isPortal && (
              <button
                className="min-w-[44px] min-h-[44px] flex items-center justify-center rounded-lg text-white/70 hover:text-white hover:bg-white/10 transition-colors"
                onClick={() => setPublicMenuOpen(true)}
                aria-label="Open menu"
              >
                <Menu size={22} />
              </button>
            )}
          </div>

          {/* ── Desktop nav row — 3-col grid keeps the logo on the true page centre ──
              Equal minmax(0,1fr) side columns ⇒ the auto centre column (logo) lands on the
              page midline regardless of the wordmark / LOGIN widths. items-center keeps the
              wordmark, nav links, logo and action button on one vertical centreline. */}
          <div
            className="hidden md:grid w-full items-center"
            style={{ gridTemplateColumns: "minmax(0,1fr) auto minmax(0,1fr)" }}
          >

            {/* Left column — wordmark pinned left, Home/About hug the centre */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3 shrink-0">
                {isPortal && user && user.role !== "driver" && (
                  <button
                    className="lg:hidden min-w-[44px] min-h-[44px] flex items-center justify-center rounded-lg text-white/70 hover:text-white hover:bg-white/10 transition-colors"
                    onClick={onMenuClick}
                    aria-label="Open navigation menu"
                  >
                    <Menu size={22} />
                  </button>
                )}
                <Link
                  to="/"
                  className="text-white leading-none"
                  style={{ fontFamily: "'RQND Pro', var(--df-display)", fontWeight: 800, letterSpacing: "0.04em", fontSize: 26 }}
                  data-testid="brand-wordmark"
                >
                  DRIVN <span className="opacity-90">FREIGHT</span>
                </Link>
              </div>

              {/* Links nearest the logo — gap-24 between them, pr-28 clearance from the logo.
                  Same set whether logged in or out so the dashboard bar matches the home bar. */}
              <div className="flex items-center gap-24 pr-28">
                <NavLink to="/" end className={({ isActive }) => `nav-link${isActive ? " active" : ""}`} data-testid="nav-home">Home</NavLink>
                <NavLink to="/about" className={({ isActive }) => `nav-link${isActive ? " active" : ""}`} data-testid="nav-about">About</NavLink>
              </div>
            </div>

            {/* Centre column — logo, dead centre of the page */}
            <Link to="/" data-testid="brand-link" className="shrink-0 justify-self-center">
              <Logo size={isPortal ? 34 : 40} withText={false} />
            </Link>

            {/* Right column — Services/Track/Driver hug the centre, LOGIN pinned right */}
            <div className="flex items-center justify-between">
              {/* Links nearest the logo — gap-24 between them, pl-28 clearance from the logo.
                  Same set whether logged in or out so the dashboard bar matches the home bar. */}
              <div className="flex items-center gap-24 pl-28">
                <NavLink to="/services" className={({ isActive }) => `nav-link${isActive ? " active" : ""}`} data-testid="nav-services">Services</NavLink>
                <NavLink to="/track" className={({ isActive }) => `nav-link${isActive ? " active" : ""}`} data-testid="nav-track">Track</NavLink>
                {user ? (
                  <NavLink
                    to="/dashboard"
                    className={({ isActive }) => `flex items-center gap-1.5 nav-link${isActive ? " active" : ""}`}
                    data-testid="nav-dashboard"
                  >
                    <LayoutDashboard size={13} />
                    Dashboard
                  </NavLink>
                ) : (
                  <Link
                    to="/driver-login"
                    className="flex items-center gap-1.5 nav-link"
                    style={{ color: "rgba(245,158,11,0.65)" }}
                    onMouseEnter={e => (e.currentTarget.style.color = "#f59e0b")}
                    onMouseLeave={e => (e.currentTarget.style.color = "rgba(245,158,11,0.65)")}
                    data-testid="nav-driver-portal"
                  >
                    <Truck size={13} />
                    Driver
                  </Link>
                )}
              </div>

              {/* Primary action — vertically centred, nudged toward the right edge */}
              <div className="flex items-center shrink-0 relative" style={{ left: 12 }}>
                {!user && (
                  <Link
                    to="/login"
                    className="pill-btn pill-btn-lime rqnd-pro flex items-center gap-2 !py-2 !px-6 sm:!py-2.5 sm:!px-7 text-xs sm:text-sm"
                    data-testid="login-link"
                  >
                    LOGIN
                  </Link>
                )}
                {user && (
                  <button
                    onClick={onLogout}
                    className="pill-btn flex items-center gap-2 !py-2 !px-4 sm:!py-2.5 sm:!px-6 text-xs sm:text-sm"
                    data-testid="logout-btn"
                  >
                    <LogOut size={15} />
                    <span className="hidden sm:inline">Logout</span>
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Mobile: logo centred when no desktop nav shown */}
          <div className="md:hidden mx-auto">
            <Link to="/" data-testid="brand-link-mobile">
              <Logo size={isPortal ? 34 : 40} />
            </Link>
          </div>

          {/* Mobile logout — pinned right (portal only; public pages use the drawer) */}
          {isPortal && user && (
            <div className="absolute right-4 sm:right-8 md:hidden">
              <button
                onClick={onLogout}
                className="pill-btn flex items-center gap-2 !py-2 !px-4 text-xs"
                data-testid="logout-btn-mobile"
              >
                <LogOut size={15} />
              </button>
            </div>
          )}
        </div>
      </header>

      {/* ── Mobile public nav drawer ───────────────────────── */}
      {!isPortal && publicMenuOpen && (
        <>
          <div
            className="sidebar-overlay md:hidden"
            onClick={() => setPublicMenuOpen(false)}
          />
          <div
            className="sidebar-drawer open md:hidden flex flex-col px-5"
            style={{
              width: "280px",
              paddingTop: "env(safe-area-inset-top, 24px)",
              background: "rgba(10, 10, 15, 0.97)",
              backdropFilter: "blur(24px)",
              WebkitBackdropFilter: "blur(24px)",
              borderRight: "1px solid rgba(255,255,255,0.07)",
            }}
          >
            <div className="flex items-center justify-between mb-8 pt-6">
              <Logo size={44} />
              <button
                onClick={() => setPublicMenuOpen(false)}
                className="p-1.5 rounded-lg text-white/50 hover:text-white"
                aria-label="Close menu"
              >
                <X size={20} />
              </button>
            </div>

            <nav className="flex flex-col gap-1">
              {[
                { to: "/", label: "Home", end: true },
                { to: "/about", label: "About" },
                { to: "/services", label: "Services" },
                { to: "/track", label: "Track" },
              ].map(({ to, label, end }) => (
                <NavLink
                  key={to}
                  to={to}
                  end={end}
                  onClick={() => setPublicMenuOpen(false)}
                  className={({ isActive }) => `sidebar-item${isActive ? " active" : ""}`}
                >
                  {label}
                </NavLink>
              ))}

              {user ? (
                <NavLink
                  to="/dashboard"
                  onClick={() => setPublicMenuOpen(false)}
                  className={({ isActive }) => `sidebar-item mt-2${isActive ? " active" : ""}`}
                  style={{ color: "#19e6c8" }}
                >
                  <LayoutDashboard size={16} />
                  Dashboard
                </NavLink>
              ) : (
                <Link
                  to="/driver-login"
                  onClick={() => setPublicMenuOpen(false)}
                  className="sidebar-item mt-2"
                  style={{ color: "#f59e0b" }}
                >
                  <Truck size={16} />
                  Driver Portal
                </Link>
              )}
            </nav>

            <div className="mt-auto pb-8 pt-6 border-t border-white/10">
              {user ? (
                <button
                  onClick={() => { setPublicMenuOpen(false); onLogout(); }}
                  className="pill-btn w-full flex items-center justify-center gap-2"
                  data-testid="drawer-logout"
                >
                  <LogOut size={16} />
                  Logout
                </button>
              ) : (
                <Link
                  to="/login"
                  onClick={() => setPublicMenuOpen(false)}
                  className="pill-btn block text-center"
                >
                  Login
                </Link>
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
}
